import { toast } from "@/components/ui/Toast/toast-store";
import {
  API_PATHS,
  ENTITY_SCHEMAS,
  MUTATIONS,
  PROTOCOL_VERSION,
  type MutationArgs,
  type MutationName,
  type ServerMessage,
  type SyncedTableName,
} from "@/lib/contract";
import { uuidv7 } from "@/lib/ids";

import { Replica } from "./persistence";
import { SyncSocket, type CloseDecision } from "./socket";
import {
  ackPending,
  addPending,
  applyServerChanges,
  dropPending,
  hydrate,
  markReady,
  resetStore,
  setStatus,
  syncStore,
  type RowChange,
} from "./store";
import type { PendingMutation, Row, SyncSession } from "./types";

/**
 * The sync engine: the replica on disk, the replica in memory, and the socket,
 * working as one.
 *
 *   start      load the replica from IndexedDB and show it immediately, then
 *              connect and ask for everything after its version
 *   delta      validate every row against the contract, write the batch to
 *              disk, then to memory, then advance the version
 *   mutate     apply locally at once, queue on disk, send if connected;
 *              resent on every reconnect until the server acknowledges it
 *
 * Everything that touches the replica runs through one serial queue: every
 * server message, and the hello that opens each connection. Two deltas cannot
 * interleave, and a reconnect cannot announce a version from a replica that a
 * resync is in the middle of throwing away.
 */

/** Server close codes, mirrored from the backend's socket module. */
const CLOSE = {
  sessionChanged: 4001,
  unauthorized: 4401,
  protocolMismatch: 4426,
} as const;

export interface EngineEvents {
  /** The session is gone: signed out elsewhere, deactivated, or expired. */
  onSignedOut: () => void;
}

export class SyncEngine {
  private replica: Replica | null = null;
  private socket: SyncSocket | null = null;
  private stopped = false;
  private resyncing = false;
  /** The version the server reported at hello; caught up once the replica reaches it. */
  private target = 0;
  private queue: Promise<void> = Promise.resolve();
  readonly session: SyncSession;
  private readonly events: EngineEvents;

  constructor(session: SyncSession, events: EngineEvents) {
    this.session = session;
    this.events = events;
  }

  async start(): Promise<void> {
    resetStore(this.session);

    this.replica = await Replica.open(this.session);
    const stored = await this.replica.load();
    if (this.stopped) return;
    hydrate(stored.base, stored.pending, stored.lastVersion);

    setStatus("connecting");
    const scheme = window.location.protocol === "https:" ? "wss" : "ws";
    this.socket = new SyncSocket(`${scheme}://${window.location.host}${API_PATHS.sync}`, {
      onOpen: () => {
        this.enqueue(() => {
          this.sendHello();
        });
      },
      onMessage: (message) => {
        // Flagged before queueing: the server closes the socket right after a
        // resync, and the close must know to reconnect at once even if the
        // queue has not reached this message yet.
        if (message.type === "resync") this.resyncing = true;
        this.enqueue(() => this.handle(message));
      },
      onClose: (code) => this.onClose(code),
    });
    this.socket.start();
  }

  stop(): void {
    this.stopped = true;
    this.socket?.stop();
    this.socket = null;
    this.replica?.close();
    this.replica = null;
  }

  /** Sign-out: stop, and delete this person's replica from the device. */
  async destroy(): Promise<void> {
    this.stopped = true;
    this.socket?.stop();
    this.socket = null;
    await this.queue;
    await this.replica?.destroy();
    this.replica = null;
    resetStore(null);
  }

  /**
   * Make a change. It is visible immediately, survives a reload or a dropped
   * connection, and reaches the server exactly once.
   */
  mutate<N extends MutationName>(name: N, args: MutationArgs<N>): void {
    // Checked here against the schema the server uses. A malformed mutation is
    // a bug in this client, so it should fail at the call site rather than sit
    // in the queue being rejected on every reconnect.
    const parsed = MUTATIONS[name].safeParse(args);
    if (!parsed.success) {
      console.error(`sync: invalid ${name} arguments`, parsed.error.issues);
      toast.error("That change couldn't be made", {
        description: parsed.error.issues[0]?.message ?? "Invalid input",
      });
      return;
    }

    const mutation: PendingMutation = {
      id: uuidv7(),
      name,
      args,
      createdAt: new Date().toISOString(),
      ackedVersion: null,
    };
    addPending(mutation);
    void this.replica?.putPending(mutation);
    this.socket?.send({ type: "mutate", mutationId: mutation.id, name, args });
  }

  private enqueue(work: () => void | Promise<void>): void {
    this.queue = this.queue.then(work).catch((error: unknown) => {
      console.error("sync: failed to apply a server message", error);
    });
  }

  private sendHello(): void {
    this.socket?.send({
      type: "hello",
      protocolVersion: PROTOCOL_VERSION,
      lastVersion: syncStore.getState().lastVersion,
    });
  }

  private async handle(message: ServerMessage): Promise<void> {
    if (this.stopped) return;

    switch (message.type) {
      case "hello_ok": {
        this.resyncing = false;
        this.target = message.currentVersion;
        const { lastVersion, pending } = syncStore.getState();
        if (lastVersion >= message.currentVersion) {
          markReady();
          setStatus("live");
        } else {
          setStatus("syncing");
        }

        // Everything the server has not acknowledged, in the order it was
        // made. Ones it already applied come straight back as acks: the server
        // remembers mutation ids, so a resend can never apply twice.
        for (const mutation of pending) {
          if (mutation.ackedVersion !== null) continue;
          this.socket?.send({
            type: "mutate",
            mutationId: mutation.id,
            name: mutation.name,
            args: mutation.args,
          });
        }
        return;
      }

      case "delta": {
        const changes = this.validate(message.changes);
        // Disk first: if the tab dies between the two, the next start reloads
        // a replica that already has these rows, rather than one that claims
        // a version it does not hold.
        await this.replica?.writeDelta(changes, message.upTo);

        const before = syncStore.getState().pending;
        applyServerChanges(changes, message.upTo);
        const after = new Set(syncStore.getState().pending.map((m) => m.id));
        const replica = this.replica;
        if (replica) {
          await Promise.all(
            before.filter((m) => !after.has(m.id)).map((m) => replica.deletePending(m.id)),
          );
        }

        if (!message.hasMore && message.upTo >= this.target) {
          markReady();
          setStatus("live");
        }
        return;
      }

      case "mutation_ack": {
        const acked = ackPending(message.mutationId, message.version);
        if (!acked) return;
        const stillPending = syncStore.getState().pending.some((m) => m.id === acked.id);
        if (stillPending) await this.replica?.putPending(acked);
        else await this.replica?.deletePending(acked.id);
        return;
      }

      case "mutation_error": {
        const dropped = dropPending(message.mutationId);
        if (dropped) await this.replica?.deletePending(dropped.id);
        toast.error("Couldn't save your change", { description: message.message });
        return;
      }

      case "protocol_mismatch":
        setStatus("reload-required");
        return;

      case "resync": {
        // The server says this replica can no longer be trusted, usually
        // because what this person may see has changed. Start from nothing.
        // Pending changes are kept: they were made by this person, and they
        // replay onto the fresh replica like onto any other.
        this.resyncing = true;
        const { pending } = syncStore.getState();
        await this.replica?.destroy();
        const fresh = await Replica.open(this.session);
        this.replica = fresh;
        await Promise.all(pending.map((m) => fresh.putPending(m)));
        resetStore(this.session);
        hydrate(syncStore.getState().base, pending, 0);
        setStatus("syncing");
        return;
      }

      case "pong":
        return;
    }
  }

  /**
   * Every incoming row is checked against the contract before it touches the
   * replica. A row that does not match is dropped with a warning; written
   * through, it would sit in IndexedDB being wrong until site data was cleared.
   */
  private validate(
    changes: readonly { table: SyncedTableName; rows: Record<string, unknown>[] }[],
  ): RowChange[] {
    return changes.map(({ table, rows }) => {
      const schema = ENTITY_SCHEMAS[table];
      const upserts: Row[] = [];
      const deletes: string[] = [];
      for (const raw of rows) {
        const parsed = schema.safeParse(raw);
        if (!parsed.success) {
          console.warn(`sync: dropped an invalid ${table} row`, parsed.error.issues);
          continue;
        }
        const row = parsed.data;
        // A soft-deleted row is how a delete reaches a client. The replica
        // holds only live rows, so it leaves.
        if (row.deletedAt !== null) deletes.push(row.id);
        else upserts.push(row);
      }
      return { table, upserts, deletes };
    });
  }

  private onClose(code: number): CloseDecision {
    switch (code) {
      case CLOSE.unauthorized:
        this.events.onSignedOut();
        return "stop";
      case CLOSE.protocolMismatch:
        setStatus("reload-required");
        return "stop";
      case CLOSE.sessionChanged:
        // Access changed under this connection (a new role, say). Reconnect
        // at once to pick up what is allowed now.
        setStatus("connecting");
        return "retry-now";
      default: {
        if (syncStore.getState().status === "reload-required") return "stop";
        if (this.resyncing) return "retry-now";
        // With a replica on screen, a drop is "offline": the board still works
        // and changes queue up. Without one there is nothing to show yet.
        setStatus(syncStore.getState().ready ? "offline" : "connecting");
        return "retry";
      }
    }
  }
}

let current: SyncEngine | null = null;

export function startEngine(session: SyncSession, events: EngineEvents): SyncEngine {
  if (
    current?.session.userId === session.userId &&
    current.session.workspaceId === session.workspaceId
  ) {
    return current;
  }
  current?.stop();
  current = new SyncEngine(session, events);
  void current.start();
  return current;
}

export function getEngine(): SyncEngine | null {
  return current;
}

export async function destroyEngine(): Promise<void> {
  const engine = current;
  current = null;
  await engine?.destroy();
}

/** Make a change from anywhere in the UI. */
export function mutate<N extends MutationName>(name: N, args: MutationArgs<N>): void {
  if (!current) {
    console.error(`sync: ${name} called with no active session`);
    return;
  }
  current.mutate(name, args);
}
